<template>
  <base-modal :title="config.titulo" :icon="config.icono" style="max-width: 640px; width: 100%;">
    <template #default>
      <!-- ── Quién ─────────────────────────────────────────────────────── -->
      <base-ficha-datos :campos="camposFicha">
        <template #valor.estado>
          <v-chip x-small dark :color="colorDeEstado(estadoActual)">
            {{ textoDeEstado(estadoActual) }}
          </v-chip>
        </template>
      </base-ficha-datos>

      <!-- ── Advertencias del dominio ──────────────────────────────────── -->
      <base-indicator
        v-if="advertencia"
        type="WARNING"
        :message="advertencia"
        class="mt-4"
      ></base-indicator>

      <base-indicator
        v-if="avisoRolDestino"
        type="INFO"
        :message="avisoRolDestino"
        class="mt-4"
      ></base-indicator>

      <!-- ── Zona de decisión ──────────────────────────────────────────── -->
      <div v-if="accionActual === 'ALTA' || accionActual === 'ROL'" class="mt-4">
        <v-select
          v-model="idRolSeleccionado"
          :items="opcionesDeRol"
          :label="accionActual === 'ALTA' ? 'ROL *' : 'NUEVO ROL *'"
          :disabled="cargando"
          :loading="cargandoRolesDestino"
          outlined
          dense
          hide-details="auto"
          prepend-inner-icon="mdi-account-key-outline"
        ></v-select>
        <div v-if="accionActual === 'ALTA'" class="nota-heredada mt-2">
          La sede se toma del registro de personal y no se puede modificar al crear el acceso.
        </div>
      </div>

      <div v-else-if="accionActual === 'SEDE'" class="mt-4">
        <v-select
          v-model="idSedeSeleccionada"
          :items="opcionesDeSede"
          label="NUEVA SEDE *"
          :disabled="cargando"
          outlined
          dense
          hide-details="auto"
          prepend-inner-icon="mdi-office-building-outline"
        ></v-select>
      </div>

      <div v-else-if="accionActual === 'DESACTIVAR'" class="mt-4">
        <v-textarea
          v-model="motivo"
          label="MOTIVO (OPCIONAL)"
          :rows="2"
          counter
          maxlength="200"
          outlined
          dense
          hide-details="auto"
          persistent-hint
          hint="Queda guardado en el registro de auditoría."
          :disabled="cargando"
        ></v-textarea>
      </div>

      <!-- ── Qué va a pasar ────────────────────────────────────────────── -->
      <div v-if="resumen" class="resumen-accion mt-4">
        <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
        {{ resumen }}
      </div>

      <!-- ── Error del intento anterior: persistente, junto al control ──── -->
      <div v-if="error" class="mt-4">
        <base-indicator type="ERROR" :message="error.mensaje"></base-indicator>
        <div v-if="accionesDeRecuperacion.length" class="d-flex justify-end flex-wrap mt-2" style="gap: 8px">
          <v-btn
            v-for="recuperacion in accionesDeRecuperacion"
            :key="recuperacion.clave"
            small
            outlined
            rounded
            color="primary"
            :disabled="cargando"
            @click="ejecutarRecuperacion(recuperacion.clave)"
          >
            <v-icon left small>{{ recuperacion.icono }}</v-icon>
            {{ recuperacion.texto }}
          </v-btn>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="d-flex w-100 justify-end pa-1">
        <v-btn
          rounded
          outlined
          color="#002245"
          class="mr-3 px-6"
          :disabled="cargando"
          @click="$emit('close')"
        >
          {{ error ? 'CERRAR' : 'CANCELAR' }}
          <v-icon right size="20">mdi-cancel</v-icon>
        </v-btn>

        <v-btn
          rounded
          depressed
          class="px-6"
          :class="config.color === 'error' ? '' : 'bg--primary'"
          :color="config.color === 'error' ? 'error' : undefined"
          :dark="config.color === 'error' && puedeConfirmar"
          :disabled="!puedeConfirmar"
          :loading="cargando"
          @click="ejecutar"
        >
          {{ config.primario }}
          <v-icon right dark size="20">{{ config.iconoPrimario }}</v-icon>
        </v-btn>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from '../UI/BaseModal.vue';
import BaseIndicator from '../UI/BaseIndicator.vue';
import BaseFichaDatos from '../UI/BaseFichaDatos.vue';
import useAccesosApi, {
  mensajeDeErrorDeAccesos,
  codigoDeError,
  datosDeError,
  esSesionInvalida,
  esRecursoNoDisponible,
} from '@/api/accesos/useAccesosApi';

// Un solo componente para las cinco acciones. No es "un componente con banderas":
// las cinco tienen exactamente la misma anatomía —identificar a la persona, tomar
// una única decisión, confirmar— y partirlo en cinco archivos sería copiar cinco
// veces la ficha, el manejo de error inline y el estado de carga.
const CONFIGURACION = {
  ALTA: {
    titulo: 'HABILITAR ACCESO AL SISTEMA',
    icono: 'mdi-key-plus',
    primario: 'HABILITAR ACCESO',
    iconoPrimario: 'mdi-check-circle',
    verbo: 'crear el acceso',
    exito: 'ACCESO CREADO',
  },
  ROL: {
    titulo: 'CAMBIAR ROL',
    icono: 'mdi-account-edit',
    primario: 'CAMBIAR ROL',
    iconoPrimario: 'mdi-check-circle',
    verbo: 'cambiar el rol',
    exito: 'ROL ACTUALIZADO',
  },
  SEDE: {
    titulo: 'CAMBIAR SEDE',
    icono: 'mdi-office-building-marker',
    primario: 'CAMBIAR SEDE',
    iconoPrimario: 'mdi-check-circle',
    verbo: 'cambiar la sede',
    exito: 'SEDE ACTUALIZADA',
  },
  DESACTIVAR: {
    titulo: 'DESACTIVAR ACCESO',
    icono: 'mdi-account-cancel',
    primario: 'DESACTIVAR ACCESO',
    iconoPrimario: 'mdi-account-cancel',
    color: 'error',
    verbo: 'desactivar el acceso',
    exito: 'ACCESO DESACTIVADO',
  },
  REACTIVAR: {
    titulo: 'REACTIVAR ACCESO',
    icono: 'mdi-account-reactivate',
    primario: 'REACTIVAR ACCESO',
    iconoPrimario: 'mdi-check-circle',
    verbo: 'reactivar el acceso',
    exito: 'ACCESO REACTIVADO',
  },
};

// Los 409 que habilitan una recuperación de un clic. La UI decide por `codigo`,
// nunca por el texto del mensaje, que cambia con cualquier corrección de redacción.
const RECUPERACIONES = {
  ACCESO_DUPLICADO: [
    { clave: 'irAUsuarios', texto: 'IR A USUARIOS CON ACCESO', icono: 'mdi-account-search-outline' },
  ],
  ACCESO_INACTIVO_EXISTENTE: [
    { clave: 'convertirAReactivar', texto: 'REACTIVAR ACCESO', icono: 'mdi-account-reactivate' },
  ],
  ROL_NO_DISPONIBLE_EN_SEDE: [
    { clave: 'convertirARol', texto: 'CAMBIAR EL ROL AHORA', icono: 'mdi-account-edit' },
  ],
};

export default {
  name: 'DialogoAccionAcceso',

  components: { BaseModal, BaseIndicator, BaseFichaDatos },

  props: {
    /** 'ALTA' | 'ROL' | 'SEDE' | 'DESACTIVAR' | 'REACTIVAR' */
    accion: { type: String, required: true },
    /** La fila del listado (usuario con acceso o personal sin acceso). */
    registro: { type: Object, required: true },
    /** Respuesta de GET /accesos/contexto. */
    contexto: { type: Object, required: true },
    /** { id_sede, nombre } de la sede que se está viendo. */
    sedeVisible: { type: Object, default: () => ({}) },
    /** [{ id_rol, nombre, nivel, asignable }] — ALTA y ROL. */
    roles: { type: Array, default: () => [] },
    /** [{ id_sede, nombre }] — solo SEDE. */
    sedes: { type: Array, default: () => [] },
  },

  data() {
    return {
      accionActual: this.accion,
      // Copia local porque las recuperaciones guiadas de los 409 la completan
      // con datos que solo llegan en el cuerpo del error (id_usuario, id_rol).
      registroActual: { ...this.registro },
      idRolSeleccionado: null,
      idSedeSeleccionada: null,
      motivo: '',
      cargando: false,
      error: null,
      // Contexto que deja el 409 ROL_NO_DISPONIBLE_EN_SEDE al convertir a modo ROL.
      sedeDestinoPendiente: null,
      rolesDestino: null,
      cargandoRolesDestino: false,
    };
  },

  computed: {
    config() {
      return CONFIGURACION[this.accionActual] || CONFIGURACION.ALTA;
    },

    estadoActual() {
      return this.registroActual.estado || this.registroActual.estado_acceso || null;
    },

    esPropiaFila() {
      return (
        this.contexto &&
        this.registroActual.unicodigo !== undefined &&
        this.registroActual.unicodigo === this.contexto.unicodigo
      );
    },

    nombreSede() {
      return (
        this.registroActual.sede_nombre ||
        this.sedeVisible.nombre ||
        (this.contexto && this.contexto.sede_nombre) ||
        '—'
      );
    },

    camposFicha() {
      const campos = [
        { clave: 'unicodigo', etiqueta: 'Unicódigo', valor: this.registroActual.unicodigo },
        { clave: 'carnet', etiqueta: 'Carnet de identidad', valor: this.registroActual.carnet },
        {
          clave: 'nombre',
          etiqueta: 'Nombre completo',
          valor: this.registroActual.nombre_completo,
          ancho: 'completo',
          destacado: true,
        },
        {
          clave: 'sede',
          etiqueta: this.accionActual === 'SEDE' ? 'Sede actual' : 'Sede',
          valor: this.nombreSede,
        },
      ];

      if (this.accionActual !== 'ALTA') {
        campos.push({
          clave: 'rol',
          etiqueta: this.accionActual === 'ROL' ? 'Rol actual' : 'Rol',
          valor: this.registroActual.rol_nombre,
        });
      }

      campos.push({ clave: 'estado', etiqueta: 'Estado actual', valor: this.estadoActual });
      return campos;
    },

    advertencia() {
      if (this.accionActual === 'DESACTIVAR') {
        return 'Al desactivar, la persona deja de poder iniciar sesión de inmediato. El acceso se puede reactivar después.';
      }
      if (this.accionActual === 'SEDE') {
        return 'Cambiar la sede actualiza también el registro de personal y reasigna el rol equivalente en la sede destino. Si ese rol no existe en la sede elegida, el cambio no se realiza.';
      }
      return '';
    },

    avisoRolDestino() {
      if (this.accionActual !== 'ROL' || !this.sedeDestinoPendiente) return '';
      return `Elegí un rol que también exista en ${this.sedeDestinoPendiente.nombre}. Después volvé a intentar el cambio de sede.`;
    },

    // Nombres de rol disponibles en la sede destino, para no ofrecer un rol que
    // haría fallar el traslado por segunda vez. Si la consulta falló, es null y
    // no se anota nada: degradar la ayuda es mejor que bloquear la pantalla.
    nombresDeRolEnDestino() {
      if (!this.rolesDestino) return null;
      return this.rolesDestino.map((rol) => rol.nombre);
    },

    opcionesDeRol() {
      return this.roles.map((rol) => {
        const esActual = this.accionActual === 'ROL' && rol.id_rol === this.registroActual.id_rol;
        const faltaEnDestino =
          this.nombresDeRolEnDestino !== null &&
          this.nombresDeRolEnDestino.indexOf(rol.nombre) === -1;

        let sufijo = '';
        if (esActual) sufijo = ' — rol actual';
        else if (!rol.asignable) sufijo = ' — requiere nivel Administrador';
        else if (faltaEnDestino && this.sedeDestinoPendiente) {
          sufijo = ` — no existe en ${this.sedeDestinoPendiente.nombre}`;
        }

        return {
          value: rol.id_rol,
          text: `${rol.nombre}${sufijo}`,
          disabled: esActual || !rol.asignable || (faltaEnDestino && !!this.sedeDestinoPendiente),
        };
      });
    },

    opcionesDeSede() {
      const idSedeActual = this.registroActual.id_sede;
      return this.sedes.map((sede) => ({
        value: sede.id_sede,
        text: `${sede.nombre}${sede.id_sede === idSedeActual ? ' — sede actual' : ''}`,
        disabled: sede.id_sede === idSedeActual,
      }));
    },

    nombreRolSeleccionado() {
      const rol = this.roles.find((item) => item.id_rol === this.idRolSeleccionado);
      return rol ? rol.nombre : '';
    },

    nombreSedeSeleccionada() {
      const sede = this.sedes.find((item) => item.id_sede === this.idSedeSeleccionada);
      return sede ? sede.nombre : '';
    },

    resumen() {
      const nombre = this.registroActual.nombre_completo || 'esta persona';
      if (this.accionActual === 'ALTA') {
        if (!this.idRolSeleccionado) return '';
        return `Se va a crear un acceso ACTIVO para ${nombre} con el rol ${this.nombreRolSeleccionado} en ${this.nombreSede}.`;
      }
      if (this.accionActual === 'ROL') {
        if (!this.idRolSeleccionado) return '';
        const rolActual = this.registroActual.rol_nombre || 'su rol actual';
        return `El rol de ${nombre} pasa de ${rolActual} a ${this.nombreRolSeleccionado}. El cambio se aplica de inmediato y queda registrado en la auditoría.`;
      }
      if (this.accionActual === 'SEDE') {
        if (!this.idSedeSeleccionada) return '';
        return `${nombre} pasa de ${this.nombreSede} a ${this.nombreSedeSeleccionada}, conservando el rol ${this.registroActual.rol_nombre || 'actual'}.`;
      }
      if (this.accionActual === 'REACTIVAR') {
        const rol = this.registroActual.rol_nombre;
        return rol
          ? `${nombre} vuelve a tener acceso con el rol ${rol}. Si necesita otro rol, cambiáselo después de reactivar.`
          : `${nombre} vuelve a tener acceso al sistema.`;
      }
      return '';
    },

    accionesDeRecuperacion() {
      if (!this.error || !this.error.codigo) return [];
      return RECUPERACIONES[this.error.codigo] || [];
    },

    // Si el 409 tiene una recuperación guiada, reintentar lo mismo va a fallar
    // igual: el camino hacia adelante es el botón de recuperación, no el primario.
    reintentarEsInutil() {
      return this.accionesDeRecuperacion.length > 0;
    },

    puedeConfirmar() {
      if (this.cargando || this.reintentarEsInutil) return false;
      if (this.accionActual === 'ALTA') return !!this.idRolSeleccionado;
      if (this.accionActual === 'ROL') {
        return !!this.idRolSeleccionado && this.idRolSeleccionado !== this.registroActual.id_rol;
      }
      if (this.accionActual === 'SEDE') {
        return !!this.idSedeSeleccionada && this.idSedeSeleccionada !== this.registroActual.id_sede;
      }
      return true;
    },
  },

  methods: {
    colorDeEstado(estado) {
      if (estado === 'ACTIVO') return 'success';
      if (estado === 'INACTIVO') return 'warning';
      if (estado === 'NO_REGISTRADO') return 'neutral-500';
      return 'neutral-400';
    },

    textoDeEstado(estado) {
      if (!estado) return '—';
      return estado === 'NO_REGISTRADO' ? 'NO REGISTRADO' : estado;
    },

    ejecutar() {
      if (!this.puedeConfirmar) return;
      // Única excepción al "sin doble confirmación": cambiar la sede propia
      // invalida la sesión del gestor (rol remapeado + menú armado con datos
      // viejos), así que se le avisa antes de que pase, no después.
      if (this.accionActual === 'SEDE' && this.esPropiaFila) {
        this.confirmarCambioDeSedePropia();
        return;
      }
      this.ejecutarConfirmado();
    },

    confirmarCambioDeSedePropia() {
      this.$store.dispatch('openModal', {
        component: 'SeguroModal',
        props: {
          title: 'CAMBIAR TU PROPIA SEDE',
          message: `Vas a cambiar tu propia sede a ${this.nombreSedeSeleccionada}. Vas a dejar de ver el personal de ${this.nombreSede} y tu menú puede cambiar. Al terminar vas a tener que iniciar sesión de nuevo.`,
          textoConfirmar: 'SÍ, CAMBIAR MI SEDE',
          colorConfirmar: 'error',
        },
        events: {
          confirm: () => {
            this.$store.dispatch('closeTopModal');
            this.ejecutarConfirmado();
          },
          cancel: () => this.$store.dispatch('closeTopModal'),
        },
      });
    },

    llamadaDeLaAccion() {
      const idUsuario = this.registroActual.id_usuario;
      if (this.accionActual === 'ALTA') {
        // `id_sede` no se envía nunca: el backend lo rechaza con 400 a propósito.
        return useAccesosApi.crearAcceso({
          unicodigo: this.registroActual.unicodigo,
          id_rol: this.idRolSeleccionado,
        });
      }
      if (this.accionActual === 'ROL') {
        return useAccesosApi.cambiarRol(idUsuario, { id_rol: this.idRolSeleccionado });
      }
      if (this.accionActual === 'SEDE') {
        return useAccesosApi.cambiarSede(idUsuario, { id_sede: this.idSedeSeleccionada });
      }
      if (this.accionActual === 'DESACTIVAR') {
        const cuerpo = { activo: 'INACTIVO' };
        // Se omite en vez de mandar "": el motivo vacío no es un motivo.
        if (this.motivo && this.motivo.trim()) cuerpo.motivo = this.motivo.trim();
        return useAccesosApi.cambiarEstado(idUsuario, cuerpo);
      }
      return useAccesosApi.cambiarEstado(idUsuario, { activo: 'ACTIVO' });
    },

    async ejecutarConfirmado() {
      this.cargando = true;
      this.error = null;
      const accionEjecutada = this.accionActual;

      try {
        const respuesta = await this.llamadaDeLaAccion();
        const datos = (respuesta && respuesta.data && respuesta.data.data) || {};

        if (accionEjecutada === 'SEDE' && this.esPropiaFila) {
          this.$emit('sede-propia-cambiada', {
            sede_nombre: this.nombreSedeSeleccionada,
          });
          return;
        }

        this.$emit('exito', {
          accion: accionEjecutada,
          mensaje: datos.sin_cambios
            ? 'EL ACCESO YA ESTABA EN ESE ESTADO'
            : CONFIGURACION[accionEjecutada].exito,
          aviso: this.avisoDeResultado(accionEjecutada, datos),
          registro: this.registroActual,
          datos,
        });
      } catch (error) {
        this.manejarErrorDeAccion(error, accionEjecutada);
      } finally {
        this.cargando = false;
      }
    },

    // `otros_accesos_activos` no es decorativo: si viene con elementos, la persona
    // sigue entrando al sistema con esos accesos y desactivar este no revoca nada.
    avisoDeResultado(accion, datos) {
      if (accion !== 'DESACTIVAR') return '';
      const otros = datos.otros_accesos_activos || [];
      if (!otros.length) return '';
      const roles = otros.map((acceso) => acceso.rol_nombre || `acceso ${acceso.id_usuario}`);
      return `Esta persona conserva otros accesos activos (${roles.join(', ')}), así que sigue pudiendo iniciar sesión.`;
    },

    manejarErrorDeAccion(error, accionEjecutada) {
      if (esSesionInvalida(error)) {
        this.$emit('sesion-invalida');
        return;
      }
      // 404 significa que lo que la UI creía sobre esa fila ya no es verdad:
      // no tiene sentido dejar el diálogo abierto sobre un registro fantasma.
      if (esRecursoNoDisponible(error)) {
        this.$emit('recurso-no-disponible', {
          mensaje: mensajeDeErrorDeAccesos(error, CONFIGURACION[accionEjecutada].verbo),
        });
        return;
      }
      this.error = {
        mensaje: mensajeDeErrorDeAccesos(error, CONFIGURACION[accionEjecutada].verbo),
        codigo: codigoDeError(error),
        datos: datosDeError(error),
      };
    },

    ejecutarRecuperacion(clave) {
      if (clave === 'irAUsuarios') {
        this.$emit('ir-a-usuarios', this.registroActual.unicodigo);
        this.$emit('close');
        return;
      }
      if (clave === 'convertirAReactivar') this.convertirAReactivar();
      if (clave === 'convertirARol') this.convertirARol();
    },

    // El diálogo NO se cierra: misma persona, misma ficha, cambia la zona de
    // decisión y el primario. Es la recuperación más importante del módulo.
    convertirAReactivar() {
      const datos = this.error.datos || {};
      const rol = this.roles.find((item) => item.id_rol === datos.id_rol);
      this.registroActual = {
        ...this.registroActual,
        id_usuario: datos.id_usuario,
        id_rol: datos.id_rol,
        rol_nombre: rol ? rol.nombre : this.registroActual.rol_nombre,
        estado: 'INACTIVO',
        estado_acceso: 'INACTIVO',
      };
      this.error = null;
      this.accionActual = 'REACTIVAR';
    },

    convertirARol() {
      const datos = this.error.datos || {};
      this.sedeDestinoPendiente = {
        id_sede: datos.id_sede_destino,
        nombre: datos.sede_destino_nombre || 'la sede destino',
      };
      this.error = null;
      this.idRolSeleccionado = null;
      this.accionActual = 'ROL';
      if (datos.id_sede_destino) this.cargarRolesDeDestino(datos.id_sede_destino);
    },

    async cargarRolesDeDestino(idSede) {
      this.cargandoRolesDestino = true;
      try {
        const respuesta = await useAccesosApi.listarRoles({ id_sede: idSede });
        this.rolesDestino = (respuesta && respuesta.data && respuesta.data.data) || [];
      } catch (e) {
        // Ayuda opcional: si no se pudo, se sigue sin la anotación por rol.
        this.rolesDestino = null;
      } finally {
        this.cargandoRolesDestino = false;
      }
    },
  },
};
</script>

<style scoped>
.nota-heredada {
  color: var(--v-neutral-600-base);
  font-size: 0.75rem;
  line-height: 1.4;
}

.resumen-accion {
  background-color: var(--v-primary-100-base);
  border-radius: 6px;
  color: var(--v-primary-600-base);
  font-size: 0.85rem;
  line-height: 1.5;
  padding: 10px 12px;
}
</style>
